/**
 * Standalone currency and country types for pricing-pages-ui-components.
 *
 * These are simple string types that don't depend on CHIRP-generated types,
 * allowing ui-components to remain self-contained for downstream consumers.
 *
 * NOTE: These intentionally don't use CHIRP-generated enums to avoid creating
 * a dependency on pricing-pages-api/__generated__/chirp for external consumers.
 */

/**
 * Valid currency code string (e.g., 'USD', 'EUR', 'GBP').
 * Excludes undefined/null and CHIRP sentinel values.
 */

/**
 * Valid country code string (e.g., 'US', 'GB', 'DE').
 * Excludes undefined/null and CHIRP sentinel values.
 */

/**
 * Type guard that checks if a value is a non-null string suitable for use as a CurrencyCode.
 *
 * Filters out undefined, null, non-strings, and the CHIRP 'UNDEFINED' sentinel value.
 *
 * @example
 * if (isCurrencyCodeString(response.currencyCode)) {
 *   return response.currencyCode;
 * }
 */
export function isCurrencyCodeString(value) {
  return value !== undefined && value !== null && typeof value === 'string' && value !== 'UNDEFINED';
}

/**
 * Extracts the string value from a CurrencyCode, filtering out non-string types.
 * Returns undefined if the value is not a string.
 *
 * Use this when you need to pass a CurrencyCode to APIs or systems that only
 * accept string values (e.g., tracking, mutations, URL params).
 *
 * @example
 * const currencyStr = getCurrencyCodeString(currencyCode);
 * if (currencyStr) {
 *   trackEvent({ currency: currencyStr });
 * }
 */
export function getCurrencyCodeString(code) {
  return typeof code === 'string' ? code : undefined;
}

/**
 * Type guard to check if a value is a valid CountryCode string.
 * Filters out undefined, null, non-strings, and the CHIRP 'UNDEFINED' sentinel value.
 */
export function isCountryCodeString(value) {
  return value !== undefined && value !== null && typeof value === 'string' && value !== 'UNDEFINED';
}